/**
 * The single home for "PUT bytes to a URL, with progress" (LF-4771).
 *
 * Every upload surface in the app — recordings audio, intake documents, matter
 * files, email-template attachments — shares exactly one invariant: it PUTs a
 * blob to a pre-signed URL and wants progress + cancel + a couple of retries.
 * That was hand-rolled 5 different ways (Scribe's `uploadToS3WithRetry` was the
 * gold standard; others had no abort, no retry, or no progress at all). This is
 * the generalised primitive they all delegate to.
 *
 * Domain-neutral on purpose — it lives in `lib/uploads/`, not
 * `lib/services/files/`, so Scribe/intake/etc. don't have to import the
 * matter-files domain module just to move bytes. It knows nothing about
 * pre-sign or upload-complete; the caller composes those around it.
 *
 * `fetch` cannot report upload progress, so this is `XMLHttpRequest`-based and
 * browser-only. A loud guard rejects any server-side call.
 */

export interface UploadProgress {
  /** 0–100 */
  percent: number;
  /** Bytes uploaded so far */
  loaded: number;
  /** Total bytes */
  total: number;
}

export interface UploadFileToUrlOptions {
  /** Sent as `Content-Type`. Defaults to `body.type || 'application/octet-stream'`. */
  contentType?: string;
  /** Progress callback, fired on each `upload.onprogress` tick. */
  onProgress?: (progress: UploadProgress) => void;
  /** Abort signal — aborting rejects with a `DOMException` named `AbortError`. */
  signal?: AbortSignal;
  /** Attempts before giving up (exponential backoff 2/4/8s). Default `3`. */
  maxRetries?: number;
}

/** The `AbortError` a cancelled upload rejects with. */
function abortError(): DOMException {
  return new DOMException('Upload aborted', 'AbortError');
}

/**
 * Is `err` an abort (from a cancelled upload / aborted signal)? Callers use
 * this to distinguish a user cancel from a genuine failure — suppress the
 * error toast, keep the rollback.
 */
export function isAbortError(err: unknown): boolean {
  if (err instanceof DOMException) return err.name === 'AbortError';
  return (
    typeof err === 'object' &&
    err !== null &&
    'name' in err &&
    (err as { name?: unknown }).name === 'AbortError'
  );
}

/** One PUT attempt. Resolves on 2xx; rejects on non-2xx / network / abort. */
function putOnce(
  url: string,
  body: Blob,
  contentType: string,
  onProgress?: (progress: UploadProgress) => void,
  signal?: AbortSignal
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Abort listener is registered per-attempt and torn down on settle so
    // retries never leak a listener (the previous hand-rolled version added
    // one on every attempt and never removed it).
    const onAbort = () => xhr.abort();
    const cleanup = () => signal?.removeEventListener('abort', onAbort);

    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);

    if (signal) signal.addEventListener('abort', onAbort, { once: true });

    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          onProgress({
            percent: Math.round((event.loaded / event.total) * 100),
            loaded: event.loaded,
            total: event.total
          });
        }
      });
    }

    xhr.addEventListener('load', () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed: storage returned ${xhr.status}`));
      }
    });
    xhr.addEventListener('error', () => {
      cleanup();
      reject(new Error('Network error during upload'));
    });
    xhr.addEventListener('abort', () => {
      cleanup();
      reject(abortError());
    });

    xhr.send(body);
  });
}

/**
 * PUT `body` to `url` with progress, cancellation and bounded retry.
 *
 * - Aborts (via `signal`) reject with an `AbortError` and are never retried.
 * - Transient failures (network / non-2xx) retry with 2/4/8s backoff up to
 *   `maxRetries` attempts.
 */
export async function uploadFileToUrl(
  url: string,
  body: Blob,
  opts: UploadFileToUrlOptions = {}
): Promise<void> {
  if (typeof XMLHttpRequest === 'undefined') {
    throw new Error(
      'uploadFileToUrl must run in the browser (XMLHttpRequest is unavailable server-side)'
    );
  }

  const { onProgress, signal, maxRetries = 3 } = opts;
  const contentType =
    opts.contentType || body.type || 'application/octet-stream';

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    // A signal aborted before (or between) attempts short-circuits.
    if (signal?.aborted) throw abortError();

    try {
      await putOnce(url, body, contentType, onProgress, signal);
      return;
    } catch (err) {
      // Never retry a cancellation.
      if (isAbortError(err) || signal?.aborted) throw err;
      if (attempt === maxRetries) throw err;
      // Exponential backoff: 2s, 4s, 8s.
      await new Promise((r) => setTimeout(r, 2000 * 2 ** (attempt - 1)));
    }
  }
}
